interface Props {
  value: number
  size?: number
  stroke?: number
  color?: string
  label?: string
  showPct?: boolean
  ariaLabel?: string
  onDark?: boolean
  textScale?: number
}

const RING_FONT = "'DM Sans', system-ui, sans-serif"

export function ProgressRing({
  value,
  size = 120,
  stroke = 12,
  color = 'var(--cf-color-orange-500)',
  label = 'progresso',
  showPct = true,
  ariaLabel,
  onDark = false,
  textScale = 1,
}: Props) {
  const clamped = Math.max(0, Math.min(100, value))
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const off = c - (clamped / 100) * c
  const trackStroke = onDark ? 'rgba(255,255,255,0.28)' : 'var(--cf-border-strong)'
  const valueFill = onDark ? '#fff' : color
  const textFill = onDark ? '#fff' : 'var(--cf-fg-primary)'
  const labelFill = onDark ? 'rgba(255,255,255,0.75)' : 'var(--cf-color-gray-500)'
  const hasLabel = Boolean(label)
  const pctFont = size * 0.28 * textScale
  const pctSuffixFont = size * 0.15 * textScale

  return (
    <svg
      className='cf-progress-ring'
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      role='img'
      aria-label={ariaLabel ?? (hasLabel ? `${clamped}% ${label}` : `${clamped}%`)}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill='none'
        stroke={trackStroke}
        strokeWidth={stroke}
      />
      <circle
        className='cf-progress-ring__progress'
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill='none'
        stroke={valueFill}
        strokeWidth={stroke}
        strokeLinecap='round'
        strokeDasharray={c}
        strokeDashoffset={off}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      {showPct && (
        <>
          <text
            x={size / 2}
            y={hasLabel ? size * 0.42 : size / 2}
            textAnchor='middle'
            dominantBaseline={hasLabel ? 'auto' : 'central'}
            fontFamily={RING_FONT}
            fontWeight={700}
            fontSize={pctFont}
            fill={textFill}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {clamped}
            <tspan fontSize={pctSuffixFont} baselineShift={hasLabel ? 'super' : undefined}>
              %
            </tspan>
          </text>
          {hasLabel && (
            <text
              x='50%'
              y={size / 2 + size * 0.2}
              textAnchor='middle'
              fontFamily={RING_FONT}
              fontWeight={600}
              fontSize={size * 0.085 * textScale}
              fill={labelFill}
            >
              {label}
            </text>
          )}
        </>
      )}
    </svg>
  )
}
